import {
  Button,
  Heading,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Portal,
  useBoolean,
  useOutsideClick,
} from "@chakra-ui/react";
import { Trans, t } from "@lingui/macro";
import {
  type User,
  UserScopeEnum,
  useAddScopedUsersToTaskMutation,
} from "@src/__generated__/graphql";
import { Icon } from "@src/components/ui-kit/Icon";
import { onError } from "@src/utils/apollo";
import { useStore } from "@src/utils/hooks";
import type { DisclosureState } from "@src/utils/mobx/states/DisclosureState";
import { userScopeToType } from "@src/utils/userScopeToType";
import { UserSelect } from "@src/widgets/UserSelect";
import { capitalize } from "lodash";
import { observer } from "mobx-react-lite";
import React, { type PropsWithChildren, useEffect, useRef } from "react";

export type AddScopedUsersPopoverProps = {
  userIds: User["id"][];
  usersIdsFormOtherScope: User["id"][];
  scope: UserScopeEnum;
  disclosure: DisclosureState<object>;
};
export const AddScopedUsersPopover = observer(function AddScopedUsersPopover({
  scope,
  userIds,
  usersIdsFormOtherScope,
  children,
  disclosure,
}: PropsWithChildren<AddScopedUsersPopoverProps>) {
  const { taskDetailModalStore, createUserModalStore, UIStore } = useStore();
  const [isLoading, setIsLoading] = useBoolean(false);
  const [user_ids, setUserIds] = React.useState<User["id"][]>([]);
  const bodyRef = useRef<HTMLDivElement>(null);

  useOutsideClick({
    ref: bodyRef,
    handler: () => {
      disclosure.onClose();
    },
  });

  const [updateTask] = useAddScopedUsersToTaskMutation({
    ...onError(),
  });

  useEffect(() => {
    setUserIds(userIds);
  }, [userIds]);

  const handleUserIdsChange = async (newUserIds: User["id"][]) => {
    setIsLoading.on();
    setUserIds(newUserIds);
    const res = await updateTask({
      variables: {
        id: taskDetailModalStore.taskId.value!,
        user_ids: [...newUserIds, ...usersIdsFormOtherScope],
        isTemplate: false,
      },
    });

    if (res.data?.updateTask) {
      UIStore.toast({
        title: t`Users updated`,
        status: "success",
      });
      taskDetailModalStore.fetchTask({ silently: true });
      taskDetailModalStore.refetchMentionsUsers();
    }

    setIsLoading.off();
  };

  return (
    <Popover gutter={16} isLazy isOpen={disclosure.isOpen}>
      <PopoverTrigger>{children}</PopoverTrigger>

      <Portal>
        <PopoverContent>
          <PopoverBody ref={bodyRef} p="5">
            <Heading mb="3" size="xs">
              <Trans>Add</Trans>
              &nbsp;
              {capitalize(scope)}
            </Heading>
            <UserSelect
              isMulti
              isLoading={isLoading}
              userType={userScopeToType(scope)}
              taskId={
                scope !== UserScopeEnum.Partner
                  ? taskDetailModalStore.taskId.value
                  : undefined
              }
              topExtraContent={
                <Button
                  mb="2"
                  p="1"
                  leftIcon={<Icon name="plus" />}
                  onClick={() =>
                    createUserModalStore.modalState.onOpen({
                      taskId: taskDetailModalStore.taskId.value,
                      userType: userScopeToType(scope),
                      onSubmit: () => taskDetailModalStore.fetchTask(),
                    })
                  }
                  variant="link"
                >
                  <Trans>Invite</Trans> {` ${capitalize(scope)}`}
                </Button>
              }
              value={user_ids}
              onChange={handleUserIdsChange}
            />
          </PopoverBody>
        </PopoverContent>
      </Portal>
    </Popover>
  );
});
